import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import { buildVehiclePreview, fetchKbbMakes, fetchKbbModels, fetchKbbTrims } from './vehicleThunks';
import { VEHICLE_REDUCER_KEY, type VehicleStoreState } from './vehicleTypes';

const initialState: VehicleStoreState = {
  makes: [],
  models: [],
  trims: [],
  preview: null,
  currentRequestId: {},
  loading: {
    makes: false,
    models: false,
    trims: false,
    preview: false,
  },
  error: {
    makes: null,
    models: null,
    trims: null,
    preview: null,
  },
  autoUpdateMiles: null,
} satisfies VehicleStoreState;

export const vehicleSlice = createAppSlice({
  name: VEHICLE_REDUCER_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    /* ---- KBB MAKES ---- */
    builder.addCase(fetchKbbMakes.pending, (state, action) => {
      state.loading.makes = true;
      state.error.makes = null;
      state.models = [];
      state.trims = [];
      state.preview = null;
      state.currentRequestId.makes = action.meta.requestId;
    });
    builder.addCase(fetchKbbMakes.fulfilled, (state, action) => {
      if (state.currentRequestId.makes !== action.meta.requestId) return;
      state.loading.makes = false;
      state.makes = action.payload;
      state.currentRequestId.makes = undefined;
    });
    builder.addCase(fetchKbbMakes.rejected, (state, action) => {
      if (state.currentRequestId.makes !== action.meta.requestId) return;
      state.loading.makes = false;
      state.error.makes = action.error.message ?? 'Failed to fetch makes from KBB';
      state.currentRequestId.makes = undefined;
    });

    /* ---- KBB MODELS ---- */
    builder.addCase(fetchKbbModels.pending, (state, action) => {
      state.loading.models = true;
      state.error.models = null;
      state.trims = [];
      state.preview = null;
      state.currentRequestId.models = action.meta.requestId;
    });
    builder.addCase(fetchKbbModels.fulfilled, (state, action) => {
      if (state.currentRequestId.models !== action.meta.requestId) return;
      state.loading.models = false;
      state.models = action.payload;
      state.currentRequestId.models = undefined;
    });
    builder.addCase(fetchKbbModels.rejected, (state, action) => {
      if (state.currentRequestId.models !== action.meta.requestId) return;
      state.loading.models = false;
      state.error.models = action.error.message ?? 'Failed to fetch models from KBB';
      state.currentRequestId.models = undefined;
    });

    /* ---- KBB TRIMS ---- */
    builder.addCase(fetchKbbTrims.pending, (state, action) => {
      state.loading.trims = true;
      state.error.trims = null;
      state.preview = null;
      state.currentRequestId.trims = action.meta.requestId;
    });
    builder.addCase(fetchKbbTrims.fulfilled, (state, action) => {
      if (state.currentRequestId.trims !== action.meta.requestId) return;
      state.loading.trims = false;
      state.trims = action.payload.trims;
      state.currentRequestId.trims = undefined;
    });
    builder.addCase(fetchKbbTrims.rejected, (state, action) => {
      if (state.currentRequestId.trims !== action.meta.requestId) return;
      state.loading.trims = false;
      state.error.trims = action.error.message ?? 'Failed to fetch trims from KBB';
      state.currentRequestId.trims = undefined;
    });

    /* ---- VEHICLE PREVIEW ---- */
    builder.addCase(buildVehiclePreview.pending, (state, action) => {
      state.loading.preview = true;
      state.error.preview = null;
      state.preview = null;
      state.currentRequestId.preview = action.meta.requestId;
    });
    builder.addCase(buildVehiclePreview.fulfilled, (state, action) => {
      if (state.currentRequestId.preview !== action.meta.requestId) return;
      state.loading.preview = false;
      state.preview = action.payload;
      state.currentRequestId.preview = undefined;
    });
    builder.addCase(buildVehiclePreview.rejected, (state, action) => {
      if (state.currentRequestId.preview !== action.meta.requestId) return;
      state.loading.preview = false;
      state.error.preview = action.error.message ?? 'Failed to fetch vehicle preview from KBB';
      state.currentRequestId.preview = undefined;
    });
  },
});

export default vehicleSlice.reducer;
